/**
 * @file
 * Traps keyboard focus in the cookie consent dialog and restores trigger focus.
 */

export function initCookieConsentFocus(Drupal) {
  "use strict";

  var lastTriggerElement = null;
  var activeDialog = null;
  var cookieDialogRequestedAt = 0;
  var managePreferencesSelector = [
    'a[href*="open_cookie_preferences=1"]',
    'button[href*="open_cookie_preferences=1"]',
    "[data-open-cookie-preferences]",
    "#securitiCmpCookiePrefBtn",
    ".cmp-pref-link",
  ].join(", ");

  function isVisible(element) {
    if (!element) {
      return false;
    }

    if (element.hidden || element.getAttribute("aria-hidden") === "true") {
      return false;
    }

    return element.getClientRects().length > 0;
  }

  function classNameToString(element) {
    if (!element) {
      return "";
    }
    return typeof element.className === "string" ? element.className : "";
  }

  function now() {
    return Date.now();
  }

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList || []);
  }

  function hasCookieSignal(element) {
    if (!element) {
      return false;
    }

    var labelledText = "";
    var labelledBy = element.getAttribute("aria-labelledby");
    if (labelledBy) {
      var labelledElement = document.getElementById(labelledBy);
      if (labelledElement) {
        labelledText = labelledElement.textContent || "";
      }
    }

    var tokenString = [
      element.id || "",
      classNameToString(element),
      element.getAttribute("aria-label") || "",
      element.getAttribute("data-testid") || "",
      labelledText,
    ].join(" ");

    return /(cookie|consent|privacy|securiti|onetrust|ot-sdk)/i.test(tokenString);
  }

  function isModalLike(element) {
    if (!element) {
      return false;
    }

    if (element.matches('[role="dialog"], [aria-modal="true"]')) {
      return true;
    }

    if (element.querySelector('[role="dialog"], [aria-modal="true"]')) {
      return true;
    }

    return element.matches(".modal.show, .modal[aria-modal='true']");
  }

  function getKnownCookieContainers() {
    return toArray(
      document.querySelectorAll(
        [
          ".securiti-consent-container",
          "[id*='securiti']",
          "[class*='securiti']",
          "#onetrust-consent-sdk",
          "#onetrust-pc-sdk",
          "#onetrust-banner-sdk",
          ".ot-sdk-container",
          "[id*='onetrust']",
          "[class*='onetrust']",
          "[id*='ot-sdk']",
          "[class*='ot-sdk']",
        ].join(", "),
      ),
    );
  }

  function scoreDialogCandidate(container) {
    var dialog =
      container.matches('[role="dialog"], [aria-modal="true"]') &&
      container.getAttribute("aria-hidden") !== "true"
        ? container
        : container.querySelector('[role="dialog"], [aria-modal="true"]');

    var target = dialog || container;
    var score = 0;

    if (dialog) {
      score += 30;
    }

    if (
      container.matches(
        ".securiti-consent-container, .ot-sdk-container, #onetrust-consent-sdk, #onetrust-pc-sdk, #onetrust-banner-sdk, [id*='onetrust'], [class*='onetrust'], [id*='securiti'], [class*='securiti'], [id*='ot-sdk'], [class*='ot-sdk']",
      )
    ) {
      score += 50;
    }

    if (hasCookieSignal(container) || hasCookieSignal(target)) {
      score += 40;
    }

    if (container.querySelector("iframe")) {
      score += 10;
    }

    if (
      cookieDialogRequestedAt > 0 &&
      now() - cookieDialogRequestedAt < 10000 &&
      isModalLike(container)
    ) {
      score += 15;
    }

    return {
      score: score,
      target: target,
    };
  }

  function getCookieDialog() {
    var candidates = getKnownCookieContainers();
    var bestCandidate = null;

    for (var i = 0; i < candidates.length; i++) {
      var container = candidates[i];
      if (!isVisible(container)) {
        continue;
      }

      var scored = scoreDialogCandidate(container);
      if (!bestCandidate || scored.score > bestCandidate.score) {
        bestCandidate = scored;
      }
    }

    if (bestCandidate && bestCandidate.score >= 30 && isVisible(bestCandidate.target)) {
      return bestCandidate.target;
    }

    if (cookieDialogRequestedAt > 0 && now() - cookieDialogRequestedAt < 10000) {
      var fallbacks = toArray(
        document.querySelectorAll('[role="dialog"], [aria-modal="true"], .modal.show'),
      ).filter(isVisible);

      for (var j = 0; j < fallbacks.length; j++) {
        if (hasCookieSignal(fallbacks[j])) {
          return fallbacks[j];
        }
      }

      if (fallbacks.length === 1) {
        return fallbacks[0];
      }
    }

    return null;
  }

  function getKnownCookieContainerFor(element) {
    if (!element) {
      return null;
    }

    var containers = getKnownCookieContainers();
    for (var i = 0; i < containers.length; i++) {
      if (containers[i] === element || containers[i].contains(element)) {
        return containers[i];
      }
    }

    return null;
  }

  function resolveTrapRoot(candidate) {
    if (!candidate) {
      return null;
    }

    var best = candidate;
    var bestCount = getFocusableElements(candidate).length;
    var cookieContainer = getKnownCookieContainerFor(candidate);

    if (cookieContainer) {
      var containerCount = getFocusableElements(cookieContainer).length;
      if (containerCount > bestCount) {
        best = cookieContainer;
        bestCount = containerCount;
      }
    }

    var parent = candidate.parentElement;
    while (parent && parent !== document.body) {
      if (isVisible(parent) && hasCookieSignal(parent)) {
        var parentCount = getFocusableElements(parent).length;
        if (parentCount > bestCount) {
          best = parent;
          bestCount = parentCount;
        }
      }
      parent = parent.parentElement;
    }

    return best;
  }

  function getFocusableElements(container) {
    if (!container) {
      return [];
    }

    var selector = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled]):not([type='hidden'])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "[tabindex]:not([tabindex='-1'])",
      "[contenteditable='true']",
      "summary",
      "iframe:not([tabindex='-1'])",
    ].join(", ");

    var all = container.querySelectorAll(selector);
    var focusable = [];

    for (var i = 0; i < all.length; i++) {
      var element = all[i];
      if (!isVisible(element)) {
        continue;
      }
      if (element.getAttribute("aria-hidden") === "true") {
        continue;
      }
      if (element.hasAttribute("inert")) {
        continue;
      }
      focusable.push(element);
    }

    return focusable;
  }

  function focusFirstElement(container) {
    if (!container) {
      return;
    }

    var focusable = getFocusableElements(container);
    var target = focusable.length > 0 ? focusable[0] : container;

    if (target === container && !container.hasAttribute("tabindex")) {
      container.setAttribute("tabindex", "-1");
    }

    try {
      target.focus();
    } catch (e) {
      // Ignore focus failures on detached/hidden elements.
    }
  }

  function shouldContainFocus() {
    if (!activeDialog || !isVisible(activeDialog)) {
      return false;
    }

    // Avoid hard containment when we can only detect a single tabbable target
    // (common with CMP content hosted in shadow DOM/iframes).
    return getFocusableElements(activeDialog).length > 1;
  }

  function focusManagePreferencesControl() {
    if (
      !lastTriggerElement ||
      !document.contains(lastTriggerElement) ||
      !isVisible(lastTriggerElement)
    ) {
      lastTriggerElement = null;
      return;
    }

    try {
      lastTriggerElement.focus();
    } catch (e) {
      // Ignore focus failures on detached/hidden elements.
    }

    lastTriggerElement = null;
  }

  function rememberTrigger(event) {
    if (!event.target || typeof event.target.closest !== "function") {
      return;
    }

    var trigger = event.target.closest(managePreferencesSelector);

    if (trigger) {
      lastTriggerElement = trigger;
      cookieDialogRequestedAt = now();
      window.setTimeout(syncDialogState, 0);
      window.setTimeout(syncDialogState, 120);
      window.setTimeout(syncDialogState, 500);
    }
  }

  function handleTabKey(event) {
    if (event.key !== "Tab" || !activeDialog || !isVisible(activeDialog)) {
      return;
    }

    var focusable = getFocusableElements(activeDialog);
    if (focusable.length === 0) {
      return;
    }

    // If CMP is iframe-driven or has a single tabbable control, do not block
    // native tab behavior to avoid trapping users on one element.
    if (focusable.length === 1) {
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var current = document.activeElement;
    var isInsideDialog = activeDialog.contains(current);

    if (event.shiftKey) {
      if (!isInsideDialog || current === first) {
        event.preventDefault();
        last.focus();
      }
      return;
    }

    if (!isInsideDialog || current === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function enforceFocusContainment(event) {
    if (!shouldContainFocus()) {
      return;
    }

    if (event.target && activeDialog.contains(event.target)) {
      return;
    }

    focusFirstElement(activeDialog);
  }

  function syncDialogState() {
    var currentDialog = getCookieDialog();
    var resolvedDialog = currentDialog ? resolveTrapRoot(currentDialog) : null;

    if (resolvedDialog && resolvedDialog !== activeDialog) {
      activeDialog = resolvedDialog;
      window.setTimeout(function () {
        if (
          lastTriggerElement &&
          activeDialog &&
          activeDialog.contains(lastTriggerElement) &&
          document.contains(lastTriggerElement) &&
          isVisible(lastTriggerElement)
        ) {
          try {
            lastTriggerElement.focus();
            return;
          } catch (e) {
            // Ignore focus failures on detached/hidden elements.
          }
        }
        focusFirstElement(activeDialog);
      }, 30);
      return;
    }

    if (!resolvedDialog && activeDialog) {
      activeDialog = null;
      window.setTimeout(focusManagePreferencesControl, 40);
      cookieDialogRequestedAt = 0;
      return;
    }

    if (cookieDialogRequestedAt > 0 && now() - cookieDialogRequestedAt > 15000) {
      cookieDialogRequestedAt = 0;
    }
  }

  Drupal.behaviors.cookieConsentFocus = {
    attach: function (context) {
      if (
        !context ||
        context !== document ||
        document.documentElement.dataset.cookieConsentFocus === "true"
      ) {
        return;
      }

      document.documentElement.dataset.cookieConsentFocus = "true";

      document.addEventListener("click", rememberTrigger, true);
      document.addEventListener("keydown", handleTabKey, true);
      document.addEventListener("focusin", enforceFocusContainment, true);

      syncDialogState();

      var observer = new MutationObserver(syncDialogState);
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          "class",
          "style",
          "hidden",
          "aria-hidden",
          "aria-modal",
          "open",
        ],
      });
    },
  };
}
